import styled from 'styled-components';
import { Row, Col, Collapse } from 'antd';
import GlobalButton from 'Components/common/components/GlobalButton';
import {
  IsMobile, IsTablet, parseColorGradientMixture, valueToObject,
} from 'Utils/UtilityFunctions';
import {
  maxMobile,
  maxTablet,
  maxWidth, primaryFont, primaryHeadingColor, retina, secondaryFont, wMBold,
} from 'Utils/utilsStyle';
import PeopleIcon from 'Components/common/assets/svgIcons/PeopleIcon';
import IndustriesWidget from 'Components/common/components/IndustriesWidget';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIndustriesEmpty, selectLoading, selectRecommendedIndustries,
  selectNewsIsCall, selectNewsFlurryObject,
} from 'Selector/IndustriesSelector';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import CompanySkeleton from 'Components/common/skeleton/CompanySkeleton';
import React, { useState } from 'react';
import { Industry } from 'Types/IndustryTypes';
import { actions } from 'Slice/IndustriesSlice';
import PeopleWidgetWithOutHolo from 'Components/common/components/PeopleWidgetWithOutHolo';
import BackIcon from 'Components/common/assets/svgIcons/BackIcon';
import { actions as flurryActions } from 'Slice/HelperSlice';

const StyleWrapper = styled.div`
  padding-bottom: 120px;
  @media all and (max-width: ${maxMobile}) {
    padding-bottom: 50px;
  }
  .fancy-box {
    border-radius: 10px;
    box-shadow: 0 0 60px rgba(0, 0, 0, 0.14);
    background: rgb(255, 255, 255);
    padding: 40px 50px 40px 50px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
     padding: 17px 25px 25px 25px;
    }
    @media all and (max-width: ${maxMobile}) {
     padding: 17px 16px 16px 16px;
    }

    h1 {
      font-family: ${secondaryFont};
      color: ${primaryHeadingColor};
      font-size: 22px;
      margin-bottom: 0;
      margin-left: 10px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 19px;
      }
    }
    p {
      font-family: ${primaryFont};
      color: #5e7079;
    }
    input {
      font-size: 16px;
      padding: 7px 12px;
    }
    label {
      font-size: 12px;
    }
    h4.recommended-name{
      color:${primaryHeadingColor};
      font-weight: ${wMBold};
      font-family:${secondaryFont};
      margin-bottom:0;
      font-size:22px;
      line-height: 38px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
       font-size: 18px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 14px;
        line-height: 19px;
      }
    }
    .ant-collapse-header, .ant-collapse-content-box {
      padding: 0px !important;
      align-items: center;
    }
    .ant-collapse-header {
      @media all and (max-width: ${maxMobile}) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .ant-collapse-extra {
        @media all and (max-width: ${maxMobile}) {
          width: 100%;
        }
        button {
          @media all and (max-width: ${maxMobile}) {
            width: 100%;
            text-align: center;
            justify-content: center;
            margin-top: 15px;
          }
        }
      }
    }
    .ant-collapse-item {
      .ant-collapse-arrow {
        display: none !important;
      }
      .heading-collapse-c {
        .heading-icon {
          display: none;
        }
      }
      .see-less-btn {
        display: none;
      }
      &.ant-collapse-item-active {
        .see-less-btn {
          display: block;
          @media all and (max-width: ${maxMobile}) {
            display: none;
          }
        }
        .user-images {
          display: none;
        }
        .heading-collapse-c {
          .heading-icon {
            display: block;
            margin-right: 15px;
            margin-top: 5px;
          }
        }
        .ant-collapse-extra {
          .see-more-btn {
            display: none;
          }
        }
      }
    }
    .user-images{
    position: relative;
    margin-right:13px;
    z-index:0;
    > div{
        padding:2px !important;
        &:nth-child(1){
          z-index:5;
        }
        &:nth-child(2){
          z-index:4;
          margin-left: -10px;
        }
        &:nth-child(3){
          z-index:3;
          margin-left: -10px;
        }
        &:nth-child(4){
          z-index:2;
          margin-left: -10px;
        }
        &:nth-child(5){
          z-index:1;
          margin-left: -10px;
        }
    }
  }
  }
`;

const CompaniesSection = function CompaniesSection() {
  const { Panel } = Collapse;
  const industries = useSelector(selectRecommendedIndustries);
  const emptyIndustries = useSelector(selectIndustriesEmpty);
  const [open, setOpen] = useState(false);
  const loading = useSelector(selectLoading);
  const dispatch = useDispatch();
  const isTab = IsTablet();
  const isMob = IsMobile();

  const flurryObject = useSelector(selectNewsFlurryObject);
  const isCalled = useSelector(selectNewsIsCall);

  const followIndustry = (industry: Industry) => {
    dispatch(actions.addingIndustry(industry));
  };

  const handleChange = () => {
    setOpen(!open);
  };

  const getRecommendedLoadingSkeleton = () => {
    const skeletons = [];
    for (let i = 0; i < 6; i += 1) {
      skeletons.push(
        <Col xxl={{ span: 4 }} xl={{ span: 4 }} lg={{ span: 6 }} className="gutter-row" key={`recommended-industry-skeleton-${i}`}>
          <CompanySkeleton />
        </Col>,
      );
    }
    return skeletons;
  };

  return (
    <StyleWrapper>
      <div className="fancy-box">
        <Collapse ghost onChange={handleChange} activeKey={emptyIndustries || open ? ['1'] : ['']}>
          <Panel
            header={(
              <div className="d-flex heading-collapse-c">
                <div className="heading-icon">
                  {isMob ? (
                    <BackIcon />
                  ) : (
                    <PeopleIcon />
                  )}
                </div>
                <div className="d-flex align-items-center user-images">
                  {
                    loading ? (
                      <>
                        {[1, 2, 3, 4, 5].map(() => <SkeletonCustom width="40px" height="40px" diameter="50%" margin="0px" />)}
                      </>
                    )
                      : industries.slice(0, 4).map((v) => (
                        <PeopleWidgetWithOutHolo
                          key={v.id}
                          diameter="40px"
                          diameterSm="40px"
                          diameterXsm="40px"
                          marginSm="0px 3px 0px 3px"
                          margin="0"
                          wrapperColor={parseColorGradientMixture([])}
                          logo={v.fullimage}
                          showPersonName
                          personForHover
                          personName={v.name}
                          industryWidget
                        />
                      ))
                  }

                </div>
                <h4
                  className="recommended-name d-flex align-items-center"
                >
                  {loading ? (<SkeletonCustom width={isTab ? '300px' : '500px'} height="20px" margin="0px" />) : 'We’ve found some Industries you might be interested in…'}

                </h4>

              </div>
            )}
            key="1"
            extra={(
              <>
                {!loading
                  ? (
                    <GlobalButton
                      buttonClassName="see-more-btn"
                      onButtonClick={() => {
                        dispatch(flurryActions.callFlurryEvent(
                          valueToObject('industry_see_recommended_clicked', 0, {
                            ...flurryObject,
                            ...isCalled && { isCalled },
                          }),
                        ));
                      }}
                      title="See Recommended"
                      bgColor="#001760"
                      borderRadius="25px"
                      PaddingSm="5px 15px 5px 10px"
                    />
                  ) : null}
                <GlobalButton buttonClassName="see-less-btn sm-hidden-c" title="See Less Recommended" bgColor="#001760" borderRadius="25px" PaddingSm="5px 15px 5px 10px" />
              </>
            )}
          >
            <div className="companiesSection">
              <Row className="Grid-width-row py-2 white-layer-col" gutter={[24, 24]}>
                {loading ? getRecommendedLoadingSkeleton() : null}
                {!loading && industries.map((industry) => (
                  <Col xxl={{ span: 4 }} xl={{ span: 4 }} lg={{ span: 6 }} className="gutter-row" key={industry.id}>
                    <IndustriesWidget
                      id={industry.id}
                      articleText={industry.name}
                      articleBanner={industry.fullimage}
                      isAdded={industry.added}
                      followIndustry={() => followIndustry(industry)}
                      followLoading={industry.loading}
                      isRecommended
                    />
                  </Col>
                ))}
              </Row>
            </div>
          </Panel>
        </Collapse>
      </div>
    </StyleWrapper>
  );
};

export default CompaniesSection;
